import { lazy, Suspense, useState } from "react";
import { Paperclip2, ImportCurve } from "iconsax-react";

const PdfViewer = lazy(() => import("../PdfViewer"));
const ImageViewer = lazy(() => import("../ImageViewer"));

interface Props {
  file?: File | null;
  existingFileUrl?: string | null;
  customFileName?: string;
  isDownloadable?: boolean;
  date?: string;
  secondaryFileUrl?: string;
  secondaryFileName?: string;
}

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".svg", ".bmp"];

function isPdfUrl(url: string): boolean {
  return url.split("?")[0].toLowerCase().endsWith(".pdf");
}

function isImageUrl(url: string): boolean {
  const path = url.split("?")[0].toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

export default function FileViewer({ file, existingFileUrl, customFileName, isDownloadable, date, secondaryFileUrl, secondaryFileName }: Props) {
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const show = Boolean(file) || Boolean(existingFileUrl);
  if (!show) return null;

  const fileUrl = file ? URL.createObjectURL(file) : existingFileUrl!;

  const fileName = file
    ? file.name
    : decodeURIComponent(existingFileUrl!.split("/").pop()?.split("?")[0] ?? "document");

  const displayName = customFileName || fileName;

  const isPdf = isPdfUrl(fileUrl);
  const isImage = isImageUrl(fileUrl);

  function humanSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function handleDownload() {
    if (!fileUrl) return;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = displayName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const hasViewer = (isPdf || isImage) && existingFileUrl;

  function handleFileClick(e: React.MouseEvent) {
    if (!existingFileUrl) return;
    if (isPdf) {
      e.preventDefault();
      setShowPdfViewer(true);
    } else if (isImage) {
      e.preventDefault();
      setShowImageViewer(true);
    }
  }

  return (
    <>
      <div className="surface-tertiary-fill rounded-[10px] p-0.5 input-box-shadow flex items-center">
        <div className="flex justify-between items-center radius-xs surface-base-fill py-3 px-2 pr-4 flex-1 min-w-0">
          {hasViewer ? (
            <button
              type="button"
              onClick={handleFileClick}
              className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer hover:opacity-90 transition-opacity duration-150 text-left">
              <span className="content-base-low shrink-0">
                <Paperclip2 size={16} color="currentColor" />
              </span>
              <p className="content-base-primary text-label-sm truncate min-w-0 flex-1">
                {displayName}
              </p>
              {file && <span className="text-label-sm content-action-neutral shrink-0">{humanSize(file.size)}</span>}
            </button>
          ) : (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer hover:opacity-90 transition-opacity duration-150">
              <span className="content-base-low shrink-0">
                <Paperclip2 size={16} color="currentColor" />
              </span>
              <p className="content-base-primary text-label-sm truncate min-w-0 flex-1">
                {displayName}
              </p>
              {file && <span className="text-label-sm content-action-neutral shrink-0">{humanSize(file.size)}</span>}
            </a>
          )}
          {date && (
            <span className="text-body-regular-sm content-base-secondary shrink-0">{date}</span>
          )}
        </div>
        {isDownloadable && (
          <button
            type="button"
            onClick={handleDownload}
            className="shrink-0 content-base-secondary px-3 cursor-pointer hover:opacity-80 transition-opacity duration-150">
            <ImportCurve size={20} color="currentColor" />
          </button>
        )}
      </div>
      {showPdfViewer && (
        <Suspense fallback={null}>
          <PdfViewer
            fileUrl={fileUrl}
            fileName={displayName}
            secondaryFileUrl={secondaryFileUrl}
            secondaryFileName={secondaryFileName}
            onClose={() => setShowPdfViewer(false)}
          />
        </Suspense>
      )}
      {showImageViewer && (
        <Suspense fallback={null}>
          <ImageViewer
            imageUrl={fileUrl}
            fileName={displayName}
            onClose={() => setShowImageViewer(false)}
          />
        </Suspense>
      )}
    </>
  );
}
